<template>
  <div>
    <p v-if="this.notification.ownershipAcresClassId === 5">
      No stumpage volume or price information is required for landowners owning no more than 10
      acres in Maine.
    </p>
    <p v-else-if="!this.landownerReport.harvest">
      You reported no harvest activity that resulted in forest products. If this is correct, no
      stumpage volume or price information is required.
    </p>
    <p v-else-if="[1, 2].indexOf(this.landownerReport.saleTypeId) === -1">
      You reported that your Method of Payment/Type of Sale was neither conducted under a contract
      for logging service (CLS) nor did the Landowner receive payment for trees cut by another
      party. If this is correct, no stumpage volume or price information is required.
    </p>
    <template v-else>
      Stumpage is being reported using
      <strong>{{getStumpagePriceType(landownerReport.stumpagePriceTypeId).toUpperCase()}}</strong>
      prices.
      <h5 class="mt-3">Standard Products</h5>
      <b-table
        ref="standardProductsTable"
        outlined
        responsive
        head-variant="primary"
        striped
        hover
        show-empty
        empty-text="No Standard Products reported."
        tbody-class="font-sm"
        :fields="standardFields"
        :items="standardProductStumpages"
      >
        <template v-slot:head()="data">
          {{data.label}}
        </template>
        <template v-slot:cell(productType)="data">
          {{getProductName(data.item.productTypeId)}}
        </template>
        <template v-slot:cell(speciesType)="data">
          {{getSpeciesTypeName(data.item.speciesTypeId)}}
        </template>
        <template v-slot:cell(volume)="data">
          {{data.item.volume.toFixed(2)}} {{getStumpageUnitName(data.item.stumpageUnitId)}}
        </template>
        <template v-slot:cell(price)="data">
          ${{data.item.price.toFixed(2)}} per
          {{getStumpageUnitProperty(data.item.stumpageUnitId, 'priceUnitName')}}
        </template>
      </b-table>
      <h5 class="mt-3">Specialty Products</h5>
      <b-table
        ref="specialtyProductsTable"
        outlined
        responsive
        head-variant="primary"
        striped
        hover
        show-empty
        empty-text="No Specialty Products reported."
        tbody-class="font-sm"
        :fields="specialtyFields"
        :items="specialtyProductStumpages"
      >
        <template v-slot:head()="data">
          {{data.label}}
        </template>
        <template v-slot:cell(productType)="data">
          {{getSpecialtyProductTypeName(data.item.specialtyProductTypeId)}}
        </template>
        <template v-slot:cell(speciesType)="data">
          {{getSpeciesTypeName(data.item.speciesTypeId)}}
        </template>
        <template v-slot:cell(volume)="data">
          {{data.item.volume.toFixed(2)}} {{getStumpageUnitName(data.item.stumpageUnitId)}}
        </template>
        <template v-slot:cell(price)="data">
          ${{data.item.price.toFixed(2)}} per
          {{getStumpageUnitProperty(data.item.stumpageUnitId, 'priceUnitName')}}
        </template>
      </b-table>
    </template>
    <b-alert v-if="landownerReport.editorCommentStumpage" variant="warning" show class="mt-3">
      <strong>Editor's Comment:</strong>
      <p class="mb-0 mt-1">{{ landownerReport.editorCommentStumpage }}</p>
    </b-alert>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import LookupModules from '@/store/moduleConfig';

export default {
  name: 'StumpageDetails',
  data() {
    return {
      standardFields: [
        { key: 'productType', label: 'Product Type' },
        { key: 'speciesType', label: 'Species/Group' },
        { key: 'volume', label: 'Volume' },
        { key: 'price', label: 'Stumpage Price' },
      ],
      specialtyFields: [
        { key: 'productType', label: 'Product Type' },
        { key: 'speciesType', label: 'Species/Group' },
        { key: 'volume', label: 'Units Harvested/Sold' },
        { key: 'price', label: 'Stumpage Price' },
      ],
    };
  },
  created() {
    this.$store.dispatch(`${LookupModules.PRODUCT_TYPES.namespace}/fetchOptions`);
    this.$store.dispatch(`${LookupModules.STUMPAGE_PRICE_TYPES.namespace}/fetchOptions`);
    this.$store.dispatch(`${LookupModules.SALE_TYPES.namespace}/fetchOptions`);
    this.$store.dispatch(`${LookupModules.SPECIES_TYPES.namespace}/fetchOptions`);
    this.$store.dispatch(`${LookupModules.STUMPAGE_UNITS.namespace}/fetchOptions`);
  },
  computed: {
    ...mapState('Notification', {
      notification: state => state.notification,
    }),
    ...mapState('LandownerReport', {
      landownerReport: state => state.landownerReport,
    }),
    ...mapState(LookupModules.PRODUCT_TYPES.namespace, {
      productTypes: state => state.options,
    }),
    ...mapGetters(LookupModules.PRODUCT_TYPES.namespace, {
      getProductName: 'getName',
    }),
    ...mapGetters(LookupModules.SPECIALTY_PRODUCT_TYPES.namespace, {
      getSpecialtyProductTypeName: 'getName',
    }),
    ...mapGetters(LookupModules.SPECIES_TYPES.namespace, {
      getSpeciesTypeName: 'getName',
    }),
    ...mapGetters(LookupModules.STUMPAGE_PRICE_TYPES.namespace, {
      getStumpagePriceType: 'getName',
    }),
    ...mapGetters(LookupModules.STUMPAGE_UNITS.namespace, {
      getStumpageUnitName: 'getName',
      getStumpageUnitProperty: 'getProperty',
    }),
    standardProductStumpages() {
      return this.landownerReport.stumpages
        .filter(s => this.specialityProductTypeIds.indexOf(s.productTypeId) === -1);
    },
    specialityProductTypeIds() {
      return this.productTypes.filter(pt => pt.specialtyProductTypeRequired).map(pt => pt.id);
    },
    specialtyProductStumpages() {
      return this.landownerReport.stumpages
        .filter(s => this.specialityProductTypeIds.indexOf(s.productTypeId) !== -1);
    },
  },
};
</script>
